import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { ROLE_LABELS, type AccessTag, type MeResponse, type Permission } from '../../api/types';
import { can, hasAccess, homeFor, ownTenantPath, useAuth, useMe } from '../../auth/AuthContext';
import { ApiStatus } from './ApiStatus';
import { Brand } from './Brand';
import { Badge, Button } from '../ui';

interface NavItem {
  to: string;
  label: string;
  /** Shown only when the signed-in user holds the permission. */
  needs?: Permission;
  /** Shown only when the API tagged the account with this access tag (Users is admin-only). */
  access?: AccessTag;
  end?: boolean;
}

/** Staff navigation, one flat list. Tenants is for every staff role, Users for admins only. */
function staffNav(me: MeResponse): NavItem[] {
  return [
    { to: homeFor(me), label: 'Overview', end: true },
    { to: '/staff/tenants', label: 'Tenants', needs: 'tenants:read' },
    { to: '/staff/users', label: 'Users', access: 'admin' },
    { to: '/staff/team', label: 'Team', needs: 'users:read' },
    { to: '/staff/status', label: 'Status', needs: 'status:read' },
    { to: '/staff/email-lab', label: 'Email lab', needs: 'provisioning:read' },
  ];
}

/** Tenant navigation; Organization (the tenant page) shows once the account belongs to a tenant. */
function tenantNav(me: MeResponse): NavItem[] {
  const organization = ownTenantPath(me);
  return [
    { to: homeFor(me), label: 'Overview', end: true },
    ...(organization ? [{ to: organization, label: 'Organization' }] : []),
    { to: '/portal/email', label: 'Email', needs: 'tenant:domains:read' },
    { to: '/portal/whatsapp', label: 'WhatsApp', needs: 'tenant:whatsapp:read' },
    { to: '/portal/subscription', label: 'Subscription', needs: 'tenant:billing:read' },
  ];
}

/**
 * Signed-in shell: a skip link, the sidebar (navigation, then the account
 * block pinned to the bottom with Account & security, the signed-in user and
 * sign out), and the page on the right. Permission gating is per link.
 */
export function AppLayout() {
  const me = useMe();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const isStaff = me.principal.actorKind === 'staff';
  const items = (isStaff ? staffNav(me) : tenantNav(me)).filter(
    (i) => (!i.needs || can(me, i.needs)) && (!i.access || hasAccess(me, i.access)),
  );

  const onSignOut = async () => {
    await signOut();
    navigate('/sign-in', { replace: true, state: { notice: 'You have been signed out.' } });
  };

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <aside className="sidebar">
        <Brand to={homeFor(me)} />
        <nav className="sidebar__nav" aria-label="Primary">
          {items.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__user">
          <nav className="sidebar__nav" aria-label="Account">
            <NavLink to="/account">Account & security</NavLink>
          </nav>
          <span className="sidebar__email" title={me.user.email}>
            {me.user.email}
          </span>
          <Badge tone={isStaff ? 'accent' : 'neutral'}>{ROLE_LABELS[me.principal.role]}</Badge>
          <Button variant="ghost" className="btn--small" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </aside>
      <div className="main">
        <main className="main__inner" id="main" tabIndex={-1}>
          <Outlet />
        </main>
        <footer className="main__footer">
          <ApiStatus />
        </footer>
      </div>
    </div>
  );
}
